import React from "react";

const PrivacyPolicyScreen = () => {
  return (
    <section className="mx-auto w-full max-w-4xl">
      <article className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm md:p-8">
        <h1 className="mb-2 text-3xl font-semibold text-slate-900">Privacy Policy</h1>
        <p className="mb-2 text-sm text-slate-500">Last updated: February 23, 2026</p>

        <h4 className="mb-2 mt-5 text-lg font-semibold text-slate-900">1. Data Controller Context</h4>
        <p className="text-sm leading-6 text-slate-700">
          This policy explains how ProShare collects, processes, stores, and protects personal data
          across account management, marketplace transactions, creator operations, and support.
        </p>

        <h4 className="mb-2 mt-5 text-lg font-semibold text-slate-900">2. Data We Collect</h4>
        <ul className="list-disc space-y-1 pl-5 text-sm leading-6 text-slate-700">
          <li>Identity and account data: name, email, hashed password, profile metadata.</li>
          <li>Transaction data: orders, purchased items, payout references, billing/shipping details.</li>
          <li>Operational data: logs, anti-fraud signals, webhook reconciliation metadata.</li>
          <li>User-generated content: creator profile text, product metadata, reviews, inquiries.</li>
          <li>
            Technical data: device/browser details, IP-derived security telemetry, cookie/session
            data, and consented analytics events such as page visits, product views, searches,
            add-to-cart actions, and purchase funnel steps.
          </li>
        </ul>

        <h4 className="mb-2 mt-5 text-lg font-semibold text-slate-900">3. How We Use Data</h4>
        <ul className="list-disc space-y-1 pl-5 text-sm leading-6 text-slate-700">
          <li>Provide core product features (auth, search, checkout, downloads, creator operations).</li>
          <li>Process payments and payouts through integrated payment providers.</li>
          <li>Prevent abuse and fraud via rate limits, velocity checks, and anomaly monitoring.</li>
          <li>Support reconciliation, auditing, and customer support investigations.</li>
          <li>Send required service communications and optional product notifications.</li>
        </ul>

        <h4 className="mb-2 mt-5 text-lg font-semibold text-slate-900">4. Legal Bases</h4>
        <p className="text-sm leading-6 text-slate-700">
          Processing is based on contractual necessity (service delivery), legal obligations
          (financial/compliance records), legitimate interests (security and abuse prevention), and
          consent where required.
        </p>

        <h4 className="mb-2 mt-5 text-lg font-semibold text-slate-900">5. Data Sharing</h4>
        <p className="text-sm leading-6 text-slate-700">
          We share data only with necessary processors and providers, such as Stripe, hCaptcha,
          hosting infrastructure, Google Analytics when analytics consent is granted, and any
          separately enabled session-insight/heatmap provider that is also gated behind consent.
          We do not sell personal data.
        </p>

        <h4 className="mb-2 mt-5 text-lg font-semibold text-slate-900">6. Retention</h4>
        <p className="text-sm leading-6 text-slate-700">
          Data is retained as long as needed for service operation, dispute handling, auditability,
          fraud prevention, and legal obligations. Closed-account and content retention may follow
          configurable cleanup policies.
        </p>

        <h4 className="mb-2 mt-5 text-lg font-semibold text-slate-900">7. Security Measures</h4>
        <ul className="list-disc space-y-1 pl-5 text-sm leading-6 text-slate-700">
          <li>Password hashing and protected authentication endpoints.</li>
          <li>CAPTCHA verification and request-level protection on sensitive flows.</li>
          <li>Server-side payment verification and webhook signature validation.</li>
          <li>Operational monitoring and reconciliation to detect payment state drift.</li>
        </ul>

        <h4 className="mb-2 mt-5 text-lg font-semibold text-slate-900">8. Your Rights</h4>
        <p className="text-sm leading-6 text-slate-700">
          Subject to local law, you may request access, correction, deletion, or portability of your
          personal data. You may also request restriction or object to certain processing activities.
        </p>

        <h4 className="mb-2 mt-5 text-lg font-semibold text-slate-900">9. Cookies, Analytics, and Consent</h4>
        <div className="space-y-3 text-sm leading-6 text-slate-700">
          <p>
            We use cookies and local session mechanisms for authentication continuity, security,
            and functional site behavior. Disabling these can break important app features.
          </p>
          <p>
            Separately, ProShare uses a consent-driven tracking preference system for non-essential
            analytics. If you allow analytics, the site can send pageview and marketplace event
            data to Google Analytics to understand navigation, catalog discovery, checkout flow,
            and purchase completion patterns.
          </p>
          <p>
            The site also includes a heatmap/session-insight baseline that remains disabled unless
            it is explicitly enabled for launch and you grant analytics consent. When disabled, no
            heatmap provider script is loaded.
          </p>
          <p>
            You can change your choice at any time using the <strong>Tracking Preferences</strong>{" "}
            control in the site footer. Choosing not to allow analytics keeps non-essential
            tracking off.
          </p>
        </div>

        <h4 className="mb-2 mt-5 text-lg font-semibold text-slate-900">10. International Transfers</h4>
        <p className="text-sm leading-6 text-slate-700">
          Where data is processed across regions, we rely on provider safeguards and contractual
          protections appropriate for cross-border transfers.
        </p>

        <h4 className="mb-2 mt-5 text-lg font-semibold text-slate-900">11. Policy Updates</h4>
        <p className="text-sm leading-6 text-slate-700">
          We may update this policy as features and compliance requirements evolve.
        </p>
      </article>
    </section>
  );
};

export default PrivacyPolicyScreen;
